import { useSEO } from "@/hooks/useSEO";
import { useSiteData } from "@/hooks/useSite";
import { LineReveal, Reveal, FadeIn } from "@/components/Motion";
import BookingWidget from "@/components/BookingWidget";
import { WEEK_ORDER, DAY_LABELS } from "@/lib/site";
import { Info } from "lucide-react";

export default function Prenota() {
  const { settings: seoSettings } = useSiteData();
  const seo = seoSettings.seo?.prenota || {};
  useSEO({
    title: seo.title || "Prenota — Richiedi il tuo appuntamento | K Nutrition Salerno",
    description:
      seo.description ||
      "Richiedi il tuo appuntamento presso K Nutrition a Salerno o per una consulenza online: scegli servizio, data e orario e invia la richiesta tramite WhatsApp.",
    path: "/prenota",
  });

  const { settings } = useSiteData();
  const p = settings.content.prenota;

  return (
    <>
      <section className="pt-28 lg:pt-36 pb-12" data-testid="prenota-header">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <FadeIn>
            <p className="flex items-center gap-3 text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium">
              <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
              Prenota
            </p>
          </FadeIn>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mt-6 mb-8">
            <LineReveal delay={0.2}>Richiedi il tuo</LineReveal>
            <LineReveal delay={0.4} className="italic text-deeptaupe">
              appuntamento.
            </LineReveal>
          </h1>
          <FadeIn delay={0.6}>
            <p className="text-base lg:text-lg leading-relaxed text-charcoal/75 max-w-2xl">{p.text}</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-16 bg-sand/20" data-testid="prenota-widget-section">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <BookingWidget />
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="prenota-schedule">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="bg-white rounded-[28px] shadow-soft p-8 h-full" data-testid="prenota-hours-card">
              <h2 className="font-display text-2xl lg:text-3xl mb-6">Orari dello studio · appuntamenti in presenza</h2>
              <ul className="space-y-2.5 text-[15px]" data-testid="prenota-hours-table">
                {WEEK_ORDER.map((day) => {
                  const windows = (settings.hours || {})[day] || [];
                  return (
                    <li key={day} className="flex justify-between gap-6 border-b border-taupe/15 pb-2.5">
                      <span className="font-medium">{DAY_LABELS[day]}</span>
                      {windows.length ? (
                        <span>{windows.map((w) => `${w.start}–${w.end}`).join(" · ")}</span>
                      ) : (
                        <span className="text-charcoal/60 text-right">Non disponibile</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-5 flex gap-2 text-[13px] leading-relaxed text-charcoal/70">
                <Info size={14} className="shrink-0 mt-0.5 text-taupe" />
                Gli orari delle richieste online sono in definizione: nella richiesta potrai indicare le tue
                preferenze e ricevere conferma su WhatsApp.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-charcoal text-ivory rounded-3xl p-8 lg:p-10 h-full flex flex-col justify-between">
              <div>
                <h2 className="font-display text-2xl lg:text-3xl mb-6">Come funziona dopo l'invio</h2>
                <ul className="space-y-4 text-sm text-ivory/80 leading-relaxed">
                  <li className="flex gap-3">
                    <span className="font-display italic text-sand text-xl leading-none">1.</span>
                    Invia la richiesta dal modulo: l'orario scelto viene bloccato temporaneamente per te.
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display italic text-sand text-xl leading-none">2.</span>
                    Avvisa K Nutrition su WhatsApp con un clic: il messaggio è già pronto con il codice richiesta.
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display italic text-sand text-xl leading-none">3.</span>
                    Ricevi la conferma dell'appuntamento con tutte le indicazioni per la visita.
                  </li>
                </ul>
              </div>
              <p className="mt-8 text-[13px] text-ivory/60 leading-relaxed" data-testid="prenota-privacy-note">
                Non ti chiediamo dati sulla tua salute in questa fase: ne parleremo direttamente in visita. Nessun
                pagamento avviene sul sito.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
